import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Hash } from "lucide-react";

interface VendorQRScannerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: string;
}

export const VendorQRScanner = ({ open, onClose, onSuccess, orderId }: VendorQRScannerProps) => {
  const [otpCode, setOtpCode] = useState("");
  const [validating, setValidating] = useState(false);

  const handleValidateOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Veuillez entrer un code de 6 chiffres");
      return;
    }

    try {
      setValidating(true);

      const { data, error } = await supabase.functions.invoke('validate-vendor-pickup-token', {
        body: { orderId, otpCode: otpCode.trim() }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(data.message || "Commande confirmée comme prête");
        onSuccess();
        onClose();
      } else {
        toast.error(data.message || "Code invalide");
      }
    } catch (error) {
      console.error("Error validating vendor OTP:", error);
      toast.error("Erreur lors de la validation");
    } finally {
      setValidating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
            <Hash className="w-5 h-5" />
            Confirmer la commande prête
          </DialogTitle>
          <DialogDescription className="text-xs md:text-sm">
            Entrez le code de 6 chiffres reçu par email du client
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Instructions visuelles */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-900 text-center">
              📧 Le client a reçu un code à 6 chiffres par email
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="otp" className="text-sm md:text-base">Code de confirmation</Label>
            <Input
              id="otp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="123456"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              disabled={validating}
              className="text-center text-2xl md:text-3xl font-mono tracking-widest h-14 md:h-16"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter' && otpCode.length === 6) {
                  handleValidateOTP();
                }
              }}
            />
            <p className="text-xs text-center text-muted-foreground">
              {otpCode.length}/6 chiffres
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={validating}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleValidateOTP}
              disabled={validating || otpCode.length !== 6}
              className="flex-1"
              size="lg"
            >
              {validating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmer la réception
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
